import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import Text from "@/components/atoms/Text";
import Heading from "@/components/atoms/Heading";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { classNames } from "@/utils/Classnames";
import { Collection, CollectionStatus } from "@/types";
import styles from "./styles.module.css";

/** Initials from a full name, e.g. "Lars Pedersen" → "LP". */
export const initialsOf = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

/* ===== Schedule board date helpers ===== */

/** A day column in the date-only Schedule board, derived from a real date. */
export interface ScheduleDay {
  /** ISO date (YYYY-MM-DD) — the key collections are bucketed against. */
  iso: string;
  /** Short weekday label, e.g. "Mon". */
  wd: string;
  /** Date number, e.g. "22". */
  dn: string;
  today: boolean;
  weekend: boolean;
}

/** Monday (ISO week start) of the date `d` falls in. */
export const startOfWeek = (d: Dayjs): Dayjs => d.subtract((d.day() + 6) % 7, "day").startOf("day");

/** Build the 7 day-columns (Mon–Sun) for the week starting at `weekStart`. */
export const buildWeek = (weekStart: Dayjs): ScheduleDay[] =>
  Array.from({ length: 7 }, (_, i) => {
    const day = weekStart.add(i, "day");
    return {
      iso: day.format("YYYY-MM-DD"),
      wd: day.format("ddd"),
      dn: day.format("D"),
      today: day.isSame(dayjs(), "day"),
      weekend: i >= 5,
    };
  });

/** Human week-range label, e.g. "22 – 28 June 2026" or "29 Jun – 5 Jul 2026". */
export const formatWeekRange = (weekStart: Dayjs): string => {
  const end = weekStart.add(6, "day");
  return weekStart.month() === end.month()
    ? `${weekStart.format("D")} – ${end.format("D MMMM YYYY")}`
    : `${weekStart.format("D MMM")} – ${end.format("D MMM YYYY")}`;
};

/** Friendly planned-date label for the detail panel, e.g. "Tue 23 Jun". */
export const formatPlanned = (iso: string): string => dayjs(iso).format("ddd D MMM");

/** Black 2-letter mono waste-code tile. */
export const TypeTile: FC<{ abbr: string; size: number }> = ({ abbr, size }) => (
  <span
    className={styles.typeTile}
    style={{ width: size, height: size, fontSize: size <= 26 ? 10 : size <= 30 ? 11 : 16 }}
  >
    {abbr}
  </span>
);

/** Mono-initials circle avatar. */
export const Avatar: FC<{ initials: string; size: number; variant?: "active" | "disabled" | "grey" }> = ({
  initials,
  size,
  variant = "active",
}) => (
  <span
    className={classNames(
      styles.avatar,
      variant === "disabled" ? styles.avatarDisabled : variant === "grey" ? styles.avatarGrey : styles.avatarActive
    )}
    style={{ width: size, height: size, fontSize: size <= 26 ? 10 : size <= 34 ? 12 : 13 }}
  >
    {initials}
  </span>
);

const STATUS_COLORS: Record<CollectionStatus, { bg: string; fg: string }> = {
  SCHEDULED: { bg: "var(--color-brand-aqua-25)", fg: "var(--color-black)" },
  IN_PROGRESS: { bg: "var(--color-processing-secondary)", fg: "var(--color-processing-primary)" },
  COMPLETED: { bg: "var(--color-completed-secondary)", fg: "var(--color-completed-primary)" },
};

/** Status pill for the Scheduled route, mapped onto the status tokens. */
export const StatusBadge: FC<{ status: CollectionStatus }> = ({ status }) => {
  const { t } = useTranslation();
  const { bg, fg } = STATUS_COLORS[status];
  return (
    <span className={styles.statusBadge} style={{ background: bg, color: fg }}>
      {t(`containers:status.${status}`)}
    </span>
  );
};

/** Status → (left bar / fill) colors for the Schedule board cards + legend. */
export const BOARD_STATUS_COLORS: Record<CollectionStatus, { bar: string; fill: string }> = {
  SCHEDULED: { bar: "var(--color-brand-aqua-100)", fill: "var(--color-brand-aqua-10)" },
  IN_PROGRESS: { bar: "var(--color-processing-primary)", fill: "var(--color-processing-secondary)" },
  COMPLETED: { bar: "var(--color-completed-primary)", fill: "var(--color-completed-secondary)" },
};

/** A collection card on the date-only Schedule board. */
export const EventCard: FC<{
  collection: Collection;
  selected: boolean;
  sortedByDriver: boolean;
  onSelect: () => void;
}> = ({ collection, selected, sortedByDriver, onSelect }) => {
  const { bar, fill } = BOARD_STATUS_COLORS[collection.status];
  return (
    <button
      type="button"
      onClick={onSelect}
      className={styles.eventCard}
      data-selected={selected}
      data-completed={collection.status === "COMPLETED"}
      style={{ ["--ev-bar" as string]: bar, ["--ev-fill" as string]: fill }}
    >
      <span className={styles.eventCardTop}>
        <span className={styles.eventTitle}>{collection.siteName}</span>
        <span className={styles.driverChip} data-active={sortedByDriver}>
          {collection.assignedDriverName ? initialsOf(collection.assignedDriverName) : "—"}
        </span>
      </span>
      <span className={styles.eventSub}>{collection.wasteTypeName}</span>
    </button>
  );
};

/** Square 2px chip group (date / day / time-window pickers). Selected = solid black. */
export const ChipGroup: FC<{
  options: string[];
  value: string | null;
  onChange: (value: string) => void;
  layout?: "wrap" | "grid";
}> = ({ options, value, onChange, layout = "wrap" }) => (
  <div className={layout === "grid" ? styles.chipGrid : styles.chipRowWrap}>
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        className={classNames(styles.chip, opt === value ? styles.chipActive : "")}
        onClick={() => onChange(opt)}
      >
        {opt}
      </button>
    ))}
  </div>
);

/** Heading + sub-heading block shared by both SubHeaders. */
export const SubHeaderTitle: FC<{ heading: string; sub: string }> = ({ heading, sub }) => {
  return (
    <div>
      <Heading order={1} styleOrder={2} strong>
        {heading}
      </Heading>
      <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={8}>
        {sub}
      </Text>
    </div>
  );
};

/** Mono section title used inside detail panels. */
export const SectionTitle: FC<{ children: ReactNode }> = ({ children }) => (
  <Text span display="technical" size="xs" strong tt="uppercase">
    {children}
  </Text>
);

/** Rationale row: icon + mono label + sentence. */
export const RationaleItem: FC<{ icon: string; label: string; text: string }> = ({ icon, label, text }) => (
  <div className={styles.rationaleRow}>
    <SVGAsset name={icon} width={17} height={17} style={{ marginTop: 1, opacity: 0.8, flexShrink: 0 }} />
    <div className={styles.rationaleText}>
      <Text span display="technical" size="xs" tt="uppercase" color="secondary">
        {label}
      </Text>
      <br />
      {text}
    </div>
  </div>
);
